import { useParams } from "react-router-dom";
import { getTenantRiskFactors, autoCreateTenantRiskFactors } from "../../../../../shared/services/apis";
import { useEffect, useState } from "react";
import RiskSkeleton from "../../../../../shared/ui/RiskScoreCard/RiskSkeleton";
import ConfirmationDialog from "../../../../../shared/ui/ConfirmationDialog";
import { toast } from "react-hot-toast";

interface Props {
  readonly RiskFactorId: string;
}

interface BrandRisk {
  id: string;
  name: string;
  score?: number;
  weight?: number;
  weightedScore?: number;
  description?: string;
  result?: string;
  resultScore?: number;
  macroFactors?: {
    title: string;
    notes: string[];
    likelihood?: string;
    impact?: string;
  }[];
}

export default function Summary({ RiskFactorId }: Props) {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [riskPerimeters, setRiskPerimeters] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState<BrandRisk | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [autoCreateLoading, setAutoCreateLoading] = useState(false);
  const [lastGenerationTime, setLastGenerationTime] = useState<number | null>(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // 1 hour cooldown between generations when existing risk factors are present
  const GENERATION_COOLDOWN = 60 * 60 * 1000; // 1 hour in milliseconds
  const STORAGE_KEY = `riskFactorGeneration_${tenantId}`;

  // Load last generation time from localStorage on component mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const timestamp = parseInt(stored, 10);
      if (!isNaN(timestamp)) {
        setLastGenerationTime(timestamp);
      }
    }
  }, [tenantId]);

  const refreshRiskScorecard = async () => {
    try {
      setLoading(true);
      const response = await getTenantRiskFactors(tenantId || "");
      const riskFactors = response.data || response;
      setRiskPerimeters(riskFactors);
    } catch (error) {
      console.error("Failed to fetch risk factors:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoCreateTenant = async () => {
    try {
      setAutoCreateLoading(true);
      const response = await autoCreateTenantRiskFactors(tenantId || "");
      
      toast.success("Risk factors auto-created successfully!");
      console.log("Auto-create response:", response);
      
      // Set the last generation time and save to localStorage if there were existing factors
      const currentTime = Date.now();
      setLastGenerationTime(currentTime);
      
      // Only save to localStorage if there were existing risk factors (for cooldown)
      if (riskPerimeters.length > 0) {
        localStorage.setItem(STORAGE_KEY, currentTime.toString());
      }
      
      // Refresh the data to show the newly created risk factors
      await refreshRiskScorecard();
      setShowConfirmDialog(false);
    } catch (error) {
      console.error("Failed to auto-create risk factors:", error);
      toast.error("Failed to auto-create risk factors. Please try again.");
    } finally {
      setAutoCreateLoading(false);
    }
  };

  // Check if generation is allowed (cooldown logic)
  const isGenerationAllowed = () => {
    if (riskPerimeters.length === 0) return true; // No cooldown if no existing factors
    if (!lastGenerationTime) return true; // First time generation
    
    const timeSinceLastGeneration = Date.now() - lastGenerationTime;
    return timeSinceLastGeneration >= GENERATION_COOLDOWN;
  };

  const getRemainingCooldownTime = () => {
    if (!lastGenerationTime || riskPerimeters.length === 0) return 0;
    
    const timeSinceLastGeneration = Date.now() - lastGenerationTime;
    const remaining = GENERATION_COOLDOWN - timeSinceLastGeneration;
    return Math.max(0, remaining);
  };

  const formatCooldownTime = (milliseconds: number) => {
    const totalMinutes = Math.ceil(milliseconds / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours > 0) {
      if (minutes > 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
      }
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    }
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  };

  // Update cooldown timer every second
  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = getRemainingCooldownTime();
      setCooldownRemaining(remaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [lastGenerationTime, riskPerimeters.length]);

  useEffect(() => {
    refreshRiskScorecard();
  }, [tenantId, RiskFactorId]);

  const formatScore = (val?: number | null): string => {
    if (val === null || val === undefined || !isFinite(val)) return "—";
    const rounded = Math.round(val * 10) / 10;
    const isWhole = Math.abs(rounded - Math.round(rounded)) < 1e-9;
    return isWhole ? String(Math.round(rounded)) : rounded.toFixed(1);
  };

  const allBrands: BrandRisk[] = (riskPerimeters || []).map((factor: any) => {
    const factorGroups = Array.isArray(factor.factorGroups)
      ? factor.factorGroups
      : [];
    const subRiskFactors = Array.isArray(factor.subRiskFactors)
      ? factor.subRiskFactors
      : [];

    // Average score across factor groups
    const collectedRatings: number[] = [];
    factorGroups.forEach((fg: any) => {
      const ratings = Array.isArray(fg.result?.ratings)
        ? fg.result.ratings
        : [];
      ratings.forEach((r: any) => {
        const numeric = isNaN(Number(r.rating ?? NaN))
          ? 0
          : Number(r.rating ?? 0);
        collectedRatings.push(numeric);
      });
    });

    const avgScore =
      collectedRatings.length > 0
        ? collectedRatings.reduce((a, b) => a + b, 0) / collectedRatings.length
        : undefined;

    // Weighted average
    let weightedSum = 0;
    let totalWeight = 0;

    subRiskFactors.forEach((sub: any) => {
      const subWeight =
        typeof sub?.weight === "number" ? Number(sub.weight) : 0;

      const groupsForSub = factorGroups.filter(
        (fg: any) => fg.subRiskFactorId === sub?.id
      );

      const subRatings: number[] = [];
      groupsForSub.forEach((g: any) => {
        const ratings = Array.isArray(g.result?.ratings)
          ? g.result.ratings
          : [];
        ratings.forEach((r: any) => {
          const numeric = isNaN(Number(r.rating ?? NaN))
            ? 0
            : Number(r.rating ?? 0);
          subRatings.push(numeric);
        });
      });

      if (subRatings.length > 0 && subWeight > 0) {
        const subAvg =
          subRatings.reduce((a, b) => a + b, 0) / subRatings.length;
        weightedSum += subAvg * subWeight;
        totalWeight += subWeight;
      }
    });

    const weightedScore =
      totalWeight > 0 ? weightedSum / totalWeight : undefined;

    // Reasonings
    const allReasonings: string[] = [];
    factorGroups.forEach((fg: any) => {
      const ratings = Array.isArray(fg.result?.ratings)
        ? fg.result.ratings
        : [];
      ratings.forEach((r: any) => {
        if (r.reasoning) allReasonings.push(String(r.reasoning));
      });
    });

    const resultText =
      allReasonings.length > 0
        ? allReasonings.join("\n\n")
        : factorGroups[0]?.result?.ratings?.[0]?.reasoning || "No result";

    const totalSubWeight = subRiskFactors.reduce(
      (acc: number, s: any) =>
        acc + (typeof s?.weight === "number" ? Number(s.weight) : 0),
      0
    );

    const avgSubWeight =
      subRiskFactors.length > 0 ? totalSubWeight / subRiskFactors.length : 0;

    return {
      id: factor?.id ?? factor?.id,
      name: factor?.name ?? factor?.name ?? "Unknown",
      score: avgScore,
      weight: avgSubWeight,
      weightedScore: weightedScore,
      description: factor?.brand?.description ?? null,
      result: resultText,
      resultScore:
        collectedRatings.length > 0
          ? Math.round(
            (collectedRatings.reduce((a, b) => a + b, 0) /
              collectedRatings.length) *
            10
          ) / 10
          : null,
      macroFactors: factorGroups.map((fg: any) => ({
        title: fg.name ?? "Group",
        notes: Array.isArray(fg.result?.ratings)
          ? fg.result.ratings.map((rr: any) => String(rr.reasoning ?? ""))
          : [],
        likelihood: "N/A",
        impact: "N/A",
      })),
    } as BrandRisk;
  });

  function getWeightedScore(
    riskScore: number | string | undefined | null,
    weight: number | string | undefined | null
  ): number {
    const safeScore = Number(riskScore) || 0;
    const safeWeight = Number(weight) || 0;
    return safeScore * safeWeight;
  }

  useEffect(() => {
    if (!selectedBrand && allBrands.length > 0) {
      setSelectedBrand(allBrands[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskPerimeters]);

  if (loading) return <RiskSkeleton />;

  const weightedScores = allBrands
    .map((b) => b.weightedScore)
    .filter((v): v is number => v !== undefined);

  const totalWeightedScore =
    weightedScores.length === 0
      ? 0
      : weightedScores.reduce((a, b) => a + b, 0) / weightedScores.length;

  return (
    <div className="bg-[#FDFDFD] rounded-2xl shadow w-full max-w-4xl mx-auto mt-4 min-h-[65vh] 2xl:mt-20 p">
      {/* Header */}
      <div className="flex justify-start ml-4 items-center border-b py-3 border-gray-200">
        <h2 className="text-3xl font-light text-gray-700 ">
          Risk Score Summary
        </h2>
       
      </div>

      {/* Score Row */}
      <div className="flex items-center justify-between border-b p-4 border-gray-200 gap-4">
        <div className="flex items-center gap-4">
          <div className="text-3xl px-6 py-2 rounded-lg border border-gray-200 bg-green-50 text-green-700">
            {formatScore(totalWeightedScore)}
          </div>
          <span className="text-gray-700">Risk Rating</span>
        </div>
         <div className="flex flex-col items-end">
          <button
            onClick={() => setShowConfirmDialog(true)}
            disabled={!isGenerationAllowed() || autoCreateLoading}
            className={`px-4 py-2 transition-colors text-sm rounded-lg font-light shadow-md border border-gray-200 ${
              isGenerationAllowed() && !autoCreateLoading
                ? "bg-[#FDFDFD]  hover:bg-[#F5F5F5]  cursor-pointer "
                : "bg-gray-100  cursor-not-allowed"
            }`}
          >
            {autoCreateLoading ? "Creating..." : "Generate Again"}
          </button>
          {!isGenerationAllowed() && cooldownRemaining > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              Next generation in {formatCooldownTime(cooldownRemaining)}
            </p>
          )}
        </div>
      </div>

      {/* Table Header */}
      <div className="w-full p-4 border-b border-gray-200 text-sm text-gray-400">
        <div className="flex justify-between">
          <span className="w-1/2">Broad Factors</span>
          <span className="w-1/6 text-center">Risk Score</span>
          <span className="w-1/6 text-center">Factor Weighting</span>
          <span className="w-1/6 text-center">Factor Score</span>
        </div>
      </div>

      {/* Brand list */}
      <div className="max-h-[60vh] overflow-y-auto">
        {(allBrands || [])
          .slice()
          .reverse()
          .map((brand) => (
            <button
              key={brand.id}
              className={`flex items-center gap-2 py-3 px-2 transition w-full text-left ${selectedBrand?.id === brand.id
                  ? "bg-gray-50 border-l-4  border-blue-400 shadow-xs"
                  : "hover:bg-gray-50 border-b-2 border-gray-200"
                }`}
              onClick={() => setSelectedBrand(brand)}
            >
              <div className="w-1/2 text-gray-700">
                {brand.name}
              </div>

              <div className="w-1/6 text-center">
                <div
                  className={`inline-flex items-center justify-center text-sm font-semibold px-2 py-1 rounded-lg border text-gray-600 border-gray-200
                    ${brand.score !== undefined && brand.score <= 3 ? "text-red-600" : ""}
                    ${brand.score !== undefined && brand.score >= 4 && brand.score <= 7
                      ? "text-yellow-600"
                      : ""
                    }
                    ${brand.score !== undefined && brand.score >= 7 ? " text-green-600" : ""}
                  `}
                >
                  {brand.score !== undefined ? formatScore(brand.score) : "—"}
                </div>
              </div>

              <div className="w-1/6 text-center">25%</div>

              <div className="w-1/6 text-center">
                {getWeightedScore(brand.score, 0.25).toFixed(1)}
              </div>
            </button>
          ))}

        {allBrands.length === 0 && (
          <div className="p-4 text-sm text-gray-500">No brands available.</div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleAutoCreateTenant}
        title="Create Risk Factors"
        message={`Make sure your documents are analyzed and ready. This will automatically create risk factor groups for this tenant based on existing risk parameters. The process may take a few moments.${
          riskPerimeters.length > 0 
            ? "\n\nNote: Since you have existing risk factors, there will be a 1-hour cooldown period before you can generate again." 
            : ""
        }`}
        confirmText="Create Risk Factors"
        cancelText="Cancel"
        type={riskPerimeters.length > 0 ? "warning" : "info"}
        loading={autoCreateLoading}
        existingData={riskPerimeters}
      />
    </div>
  );
}
